import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _objectSpread from "@babel/runtime/helpers/objectSpread2";
import _reduce from "lodash/reduce";
import _set from "lodash/set";
import _each from "lodash/each";
import _findIndex from "lodash/findIndex";
import _find from "lodash/find";
import _cloneDeep from "lodash/cloneDeep";
import _get from "lodash/get";
import "core-js/modules/es6.array.slice.js";
import BigNumber from 'bignumber.js';
import { isUnauthenticateAction } from "../utils/auth";
import { signin, signinWithOneTimeToken, register, authenticateWithGoogle, authenticateWithCorpIdp, authenticateWithApple, requestIdentity, preloadIdentity, createTUCustomer, setTUAuthentication, deactivateTUCustomer, enrollTUCustomer, signinWithRefreshToken, refreshTUCreditProfile, updateProfileSuccess, readProfileSuccess, loginToProvider, getProviderAccountStatus, attemptProviderMFA, acknowledgeTOS, updateFinancialAccountVisibility, setSelfReportedData, updateSubscriptionsOptimistic, updateProfileOptimistic, refreshProviderAccount, deleteProviderAccount, updateDebtOnboardingProfile, readYodleeProfile } from "../actions";
import { getProviderAccountStateIdFromProviderAccount } from "../selectors/yodlee";
import { yodlee as yodleeUtils } from "../utils";
import { ProviderAccountState } from "../constants";

var updateAllProfiles = function updateAllProfiles(state, payload) {
  var meta = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  var enableHeadersAndMeta = meta.enableHeadersAndMeta,
      _meta$profilesToExclu = meta.profilesToExclude,
      profilesToExclude = _meta$profilesToExclu === void 0 ? [] : _meta$profilesToExclu;
  var dataPayload = enableHeadersAndMeta ? payload.data : payload;

  var profiles = _get(dataPayload, 'identity.profiles');

  profilesToExclude.forEach(function (profileName) {
    profiles[profileName] = state[profileName];
  });
  return _objectSpread({}, profiles);
};

var updateSingleProfile = function updateSingleProfile(state, name, data) {
  var newState = _objectSpread({}, state);

  newState[name] = data;
  return newState;
};

var updateMultipleProfiles = function updateMultipleProfiles(state, names, data) {
  var newState = _cloneDeep(state);

  names.forEach(function (name, i) {
    newState[name] = data[i];
  });
  return newState;
};

var updateSingleProfileOptimistic = function updateSingleProfileOptimistic(state, name, data) {
  var newState = _objectSpread({}, state);

  newState[name] = _objectSpread(_objectSpread({}, newState[name]), data);
  return newState;
};

var updateYodleeAccountVisibility = function updateYodleeAccountVisibility(state, financialAccountId, nwEnabled) {
  var yodleeProfile = _get(state, 'yodlee_profile');

  var providerAccounts = _get(state, 'yodlee_profile.provider_accounts', []);

  var providerAccount = _find(providerAccounts, function (pa) {
    return _find(_get(pa, 'accounts'), function (account) {
      return account.id === financialAccountId;
    });
  });

  var financialAccount = _find(_get(providerAccount, 'accounts', []), function (account) {
    return account.id === financialAccountId;
  });

  if (!providerAccount || !financialAccount) {
    return state;
  }

  if (financialAccount.nw_enabled === nwEnabled) {
    return state;
  }

  var providerAccountStateId = getProviderAccountStateIdFromProviderAccount(providerAccount);

  var providerAccountIndex = _findIndex(providerAccounts, function (account) {
    return _get(account, 'provider_account_state.id') === providerAccountStateId;
  });

  var providerAccountPosition = providerAccountIndex === -1 ? providerAccounts.length : providerAccountIndex;

  var accounts = _get(providerAccount, 'accounts', []);

  var accountIndex = _findIndex(accounts, function (account) {
    return _get(account, 'id') === financialAccountId;
  });

  var accountPosition = accountIndex === -1 ? accounts.length : accountIndex;
  var accountType = financialAccount.yodlee_account_type;
  var field = yodleeUtils.getSumFieldForAccountType(financialAccount.yodlee_account_type);
  var providerSums = {};
  var totalSums = {}; // Recalculate all of the summaries on both the providerAccount and the yodleeAccount as a whole

  if (field) {
    // Get all summary fields that should be updated
    var sumFieldsToUpdate = [field, accountType === 'loan' || accountType === 'creditCard' ? 'sum_debts' : 'sum_assets']; // Given the original summary value, return the new sum after the account has been
    // hidden / shown.

    var getUpdatedSum = function getUpdatedSum(originalSum) {
      return nwEnabled ? new BigNumber(originalSum).plus(financialAccount.balance).toNumber() : new BigNumber(originalSum).minus(financialAccount.balance).toNumber();
    }; // Update the summary values


    _each(sumFieldsToUpdate, function (sumField) {
      providerSums[sumField] = getUpdatedSum(providerAccount[sumField]);
      totalSums[sumField] = getUpdatedSum(yodleeProfile[sumField]);
    });
  }

  return _objectSpread(_objectSpread({}, state), {}, {
    yodlee_profile: _objectSpread(_objectSpread({}, state.yodlee_profile), {}, {
      provider_accounts: [].concat(_toConsumableArray(providerAccounts.slice(0, providerAccountPosition)), [_objectSpread(_objectSpread({}, providerAccount), {}, {
        accounts: [].concat(_toConsumableArray(accounts.slice(0, accountPosition)), [_objectSpread(_objectSpread({}, financialAccount), {}, {
          nw_enabled: nwEnabled
        })], _toConsumableArray(accounts.slice(accountPosition + 1)))
      }, providerSums)], _toConsumableArray(providerAccounts.slice(providerAccountPosition + 1)))
    }, totalSums)
  });
};

var updateYodleeProviderAccountState = function updateYodleeProviderAccountState(state, payload) {
  // Some actions do not return the provider_account_state
  // so we need to use the provider_id returned from the endpoints to find the provider account.
  var providerId = payload.provider_id;

  var providerAccounts = _get(state, 'yodlee_profile.provider_accounts', []);

  var index = _findIndex(providerAccounts, function (account) {
    return _get(account, 'provider_account_state.provider.id') === providerId;
  });

  var position = index === -1 ? providerAccounts.length : index;
  var existingProviderAccount = index === -1 ? {} : providerAccounts[index];
  return _objectSpread(_objectSpread({}, state), {}, {
    yodlee_profile: _objectSpread(_objectSpread({}, state.yodlee_profile), {}, {
      provider_accounts: [].concat(_toConsumableArray(providerAccounts.slice(0, position)), [_objectSpread(_objectSpread({}, existingProviderAccount), {}, {
        provider_account_state: payload
      })], _toConsumableArray(providerAccounts.slice(position + 1)))
    })
  });
};

var updateYodleeProviderRefreshing = function updateYodleeProviderRefreshing(state, meta, isRefreshing) {
  var providerAccountStateId = meta.providerAccountStateId;

  var providerAccounts = _get(state, 'yodlee_profile.provider_accounts', []);

  var index = _findIndex(providerAccounts, function (account) {
    return _get(account, 'provider_account_state.id') === providerAccountStateId;
  });

  var position = index === -1 ? providerAccounts.length : index;
  var existingProviderAccount = index === -1 ? {} : providerAccounts[index];

  var existingProviderAccountState = _get(existingProviderAccount, 'provider_account_state');

  var newProviderAccountStateState = isRefreshing ? ProviderAccountState.LOGIN_IN_PROGRESS : _get(existingProviderAccountState, 'previousRefreshingState');
  var newProviderAccountStatePreviousRefreshingState = isRefreshing ? _get(existingProviderAccountState, 'state') : undefined;

  var newProviderAccountState = _objectSpread(_objectSpread({}, existingProviderAccountState), {}, {
    state: newProviderAccountStateState,
    previousRefreshingState: newProviderAccountStatePreviousRefreshingState,
    is_refreshing: isRefreshing
  });

  return _objectSpread(_objectSpread({}, state), {}, {
    yodlee_profile: _objectSpread(_objectSpread({}, state.yodlee_profile), {}, {
      provider_accounts: [].concat(_toConsumableArray(providerAccounts.slice(0, position)), [_objectSpread(_objectSpread({}, existingProviderAccount), {}, {
        provider_account_state: newProviderAccountState
      })], _toConsumableArray(providerAccounts.slice(position + 1)))
    })
  });
};

var updateYodleeAccountSelfReportedData = function updateYodleeAccountSelfReportedData(state, _ref) {
  var account = _ref.account;

  var yodleeProfile = _cloneDeep(_get(state, 'yodlee_profile')); // iterate over each provider account


  _each(yodleeProfile.provider_accounts, function (providerAccount) {
    // find the index of matching account
    var matchIndex = _findIndex(providerAccount.accounts, function (profileAccount) {
      return profileAccount.id === account.id;
    }); // update the account with the api response (api returns full account)
    // eslint-disable-next-line no-param-reassign


    if (matchIndex > -1) providerAccount.accounts[matchIndex] = account;
  });

  return _objectSpread(_objectSpread({}, state), {}, {
    yodlee_profile: yodleeProfile
  });
};

var authEnrollTUSuccess = function authEnrollTUSuccess(state, payload) {
  var newState;

  if (payload.questions) {
    // This is the loop case, we should ask the user for additional answers
    newState = updateSingleProfile(state, 'tu_authentication_status_profile', payload);
  } else {
    // we get the tu status profile back on success - manually updating profile
    // and setting status to ACTIVE so step advances
    newState = updateSingleProfile(state, 'tu_credit_report_profile', payload);

    _set(newState, 'tu_authentication_status_profile.status', 'ACTIVE');

    _set(newState, 'tu_authentication_status_profile.questions', null);
  }

  return newState;
};

var rollbackSubscriptionsData = function rollbackSubscriptionsData(state, subscriptionChanges) {
  var oldSubscriptions = _get(state, 'subscription_profile_v3');

  var revertedChanges = _reduce(subscriptionChanges, function (result, val, key) {
    return _objectSpread(_objectSpread({}, result), {}, _defineProperty({}, key, !val));
  }, {});

  var newSubscriptions = _objectSpread(_objectSpread({}, oldSubscriptions), revertedChanges);

  return updateSingleProfile(state, 'subscription_profile_v3', newSubscriptions);
};

export default (function () {
  var state = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var action = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  var payload = action.payload,
      meta = action.meta;

  if (isUnauthenticateAction(action)) {
    return {};
  }

  switch (action.type) {
    case signin.SUCCESS_TYPE:
    case signinWithOneTimeToken.SUCCESS_TYPE:
    case register.SUCCESS_TYPE:
    case authenticateWithGoogle.SUCCESS_TYPE:
    case authenticateWithApple.SUCCESS_TYPE:
    case authenticateWithCorpIdp.SUCCESS_TYPE:
    case signinWithRefreshToken.SUCCESS_TYPE:
    case requestIdentity.SUCCESS_TYPE:
      return updateAllProfiles(state, payload, meta);

    case refreshTUCreditProfile.START_TYPE:
      {
        if (meta.shouldPurge) return updateSingleProfile(state, 'tu_credit_report_profile');
        return state;
      }

    case refreshTUCreditProfile.SUCCESS_TYPE:
      {
        return updateSingleProfile(state, 'tu_credit_report_profile', payload);
      }

    case preloadIdentity.getType():
      {
        var profiles = _get(payload, 'IdentityStore.currentIdentity.profiles'); // // mimic the data normally comes back from the api for signin/reg


        return updateAllProfiles(state, {
          identity: {
            profiles: profiles
          }
        });
      }

    case readProfileSuccess.getType():
    case updateProfileSuccess.getType():
    case readYodleeProfile.SUCCESS_TYPE:
      {
        var profileType = payload.profileType,
            data = payload.data;
        return updateSingleProfile(state, profileType, data);
      }

    case deactivateTUCustomer.SUCCESS_TYPE:
      {
        return updateSingleProfile(state, 'tu_authentication_status_profile', payload);
      }

    case setTUAuthentication.SUCCESS_TYPE:
    case enrollTUCustomer.SUCCESS_TYPE:
      return authEnrollTUSuccess(state, payload);

    case createTUCustomer.SUCCESS_TYPE:
      {
        return updateSingleProfile(state, 'tu_authentication_status_profile', payload);
      }

    case createTUCustomer.FAIL_TYPE:
      {
        var _data = payload.tu_authentication_status_profile;
        return updateSingleProfile(state, 'tu_authentication_status_profile', _data);
      }

    case acknowledgeTOS.SUCCESS_TYPE:
      {
        var tosAcks = _get(state, 'terms_of_service_profile.tos_acknowledgements') || [];
        tosAcks.push(meta.tosEntry);
        return updateSingleProfile(state, 'terms_of_service_profile.tos_acknowledgements', tosAcks);
      }

    case updateDebtOnboardingProfile.SUCCESS_TYPE:
      {
        return updateMultipleProfiles(state, ['debt_profile', 'financial_profile'], [payload.debt_profile, payload.financial_profile]);
      }
    // *** Yodlee ***

    case loginToProvider.SUCCESS_TYPE:
    case getProviderAccountStatus.SUCCESS_TYPE:
    case attemptProviderMFA.SUCCESS_TYPE:
    case refreshProviderAccount.SUCCESS_TYPE:
      return updateYodleeProviderAccountState(state, payload);

    case updateFinancialAccountVisibility.START_TYPE:
      return updateYodleeAccountVisibility(state, meta.id, meta.nwEnabled);

    case updateFinancialAccountVisibility.FAIL_TYPE:
      return updateYodleeAccountVisibility(state, meta.id, !meta.nwEnabled);

    case refreshProviderAccount.START_TYPE:
      return updateYodleeProviderRefreshing(state, meta, true);

    case refreshProviderAccount.FAIL_TYPE:
      return updateYodleeProviderRefreshing(state, meta, false);

    case deleteProviderAccount.SUCCESS_TYPE:
      {
        var yodleeProfile = payload.yodlee_profile;

        if (yodleeProfile) {
          return updateSingleProfile(state, 'yodlee_profile', yodleeProfile);
        }

        return state;
      }

    case setSelfReportedData.SUCCESS_TYPE:
      return updateYodleeAccountSelfReportedData(state, payload);
    // *** Optimistic email subscriptions update ***

    case updateSubscriptionsOptimistic.getType():
      return updateSingleProfile(state, 'subscription_profile_v3', payload);

    case updateSubscriptionsOptimistic.ROLLBACK_TYPE:
      {
        var subscriptionChanges = action.meta;
        return rollbackSubscriptionsData(state, subscriptionChanges);
      }
    // *** Optimistic profile update ***

    case updateProfileOptimistic.getType():
      {
        var _profileType = payload.profileType,
            _data2 = payload.data;
        return updateSingleProfileOptimistic(state, _profileType, _data2);
      }

    case updateProfileOptimistic.ROLLBACK_TYPE:
      {
        var _action$meta = action.meta,
            _profileType2 = _action$meta.profileType,
            oldData = _action$meta.oldData;
        return updateSingleProfile(state, _profileType2, oldData);
      }

    default:
      return state;
  }
});